import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  getReservations,
  submitCancellation,
} from '../../components/day-cancellation/CancellationAPI';
import { metricsWrapper as metrics } from '../../common/metrics';
import { useDepartureStore } from './departure';
import { useReservationsStore } from './reservations';

export const useCancellationStore = defineStore('cancellation', () => {
  /* State */
  const stepIdx = ref(0);
  const firstName = ref('');
  const lastName = ref('');
  const reservationId = ref('');
  const email = ref('');
  const cancellationReason = ref('');
  const isCancelSuccess = ref(false);
  const isCancelError = ref(false);
  const isLoadingReservationLookup = ref(false);
  const isLoadingConfirmCancellation = ref(false);
  const isStepError = ref(false);
  const departure = useDepartureStore();
  const reservations = useReservationsStore();

  /* Getters */
  const isFirstStep = computed(() => stepIdx.value === 0);
  const currentStepNum = computed(() => stepIdx.value + 1);
  const isCancelled = computed(() => {
    const amountOfCancelledOrTransferred =
      reservations.items.length > 0
        ? reservations.items.reduce(
            (prev, cur) => prev + (cur.reservationStatus.toLowerCase() !== 'confirmed' ? 1 : 0),
            0,
          )
        : -1;
    return amountOfCancelledOrTransferred === reservations.items.length;
  });

  /* Actions */
  const lookupReservation = () => {
    isLoadingReservationLookup.value = true;
    const mappedReservationId = `RES-${reservationId.value}`;
    getReservations({
      firstName: firstName.value,
      lastName: lastName.value,
      reservationId: mappedReservationId,
      email: email.value,
    })
      .then((res) => {
        if (!res.reservations || res.reservations.length === 0) {
          throw Error('no reservations');
        }
        metrics.link({ linkName: 'day-cancellation:ReservationLookup_success' });
        reservations.items = res.reservations;
        departure.name = res.departure.name;
        departure.startDate = res.departure.startDate;
        departure.startTime = res.departure.startTime;
        departure.location = res.departure.location;
        departure.isFree = res.departure.free;
        stepIdx.value += 1;
        isStepError.value = false;
      })
      .catch((err) => {
        if (err.message === 'no reservations') {
          metrics.link({ linkName: 'day-cancellation:ReservationLookup_no reservations' });
        } else {
          metrics.link({ linkName: 'day-cancellation:ReservationLookup_system error' });
        }
        isStepError.value = true;
      })
      .finally(() => {
        isLoadingReservationLookup.value = false;
      });
  };

  const cancelReservation = () => {
    const payload = {
      reservations: reservations.selected.map((r) => ({
        firstName: r.firstName,
        lastName: r.lastName,
        hashedReservationId: r.hashedReservationId,
      })),
      cancelReason: cancellationReason.value,
    };
    isLoadingConfirmCancellation.value = true;
    submitCancellation(payload)
      .then(() => {
        metrics.link({ linkName: 'day-cancellation:ConfirmCancellation_success' });
        isCancelSuccess.value = true;
        isCancelError.value = false;
      })
      .catch(() => {
        metrics.link({ linkName: 'day-cancellation:ConfirmCancellation_system error' });
        isCancelSuccess.value = false;
        isCancelError.value = true;
      })
      .finally(() => {
        isLoadingConfirmCancellation.value = false;
      });
  };

  const reset = () => {
    stepIdx.value = 0;
    firstName.value = '';
    lastName.value = '';
    reservationId.value = '';
    email.value = '';
    cancellationReason.value = '';
    isCancelSuccess.value = false;
    isCancelError.value = false;
    isLoadingReservationLookup.value = false;
    isLoadingConfirmCancellation.value = false;
    isStepError.value = false;
    departure.name = '';
    departure.startDate = '';
    departure.startTime = '';
    departure.location = '';
    reservations.items = [];
    reservations.selected = [];
  };

  return {
    stepIdx,
    firstName,
    lastName,
    reservationId,
    email,
    cancellationReason,
    isCancelSuccess,
    isCancelError,
    reservations,
    isLoadingReservationLookup,
    isLoadingConfirmCancellation,
    isStepError,
    isFirstStep,
    isCancelled,
    currentStepNum,
    lookupReservation,
    cancelReservation,
    reset,
  };
});
